import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

const useGradingSystems = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [gradingSystems, setGradingSystems] = useState([]);
  const [grades, setGrades] = useState([]);
  const [calculationResult, setCalculationResult] = useState(null);

  const fetchGradingSystems = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.academics.grading_systems,
        null,
        navigate,
      );
      if (response.ok) {
        setGradingSystems(response.data || []);
        return response.data;
      } else {
        Utilities.showError?.(response, "Failed to fetch grading systems");
        message.error(response.msg || "Failed to fetch grading systems");
        return null;
      }
    } catch (error) {
      console.error("Error fetching grading systems:", error);
      message.error("An error occurred while fetching grading systems");
      return null;
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchGrades = useCallback(
    async (gradingSystemId) => {
      setLoading(true);
      try {
        const url = gradingSystemId
          ? `${domainSettings.urls.academics.grades}?grading_system=${gradingSystemId}`
          : domainSettings.urls.academics.grades;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          setGrades(response.data || []);
          return response.data;
        } else {
          message.error(response.msg || "Failed to fetch grades");
          return null;
        }
      } catch (error) {
        console.error("Error fetching grades:", error);
        message.error("An error occurred while fetching grades");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const addGradeToSystem = useCallback(
    async (gradingSystemId, data) => {
      setLoading(true);
      try {
        const url =
          domainSettings.urls.academics.grading_system_add_grade.replace(
            ":id",
            gradingSystemId,
          );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          data,
          navigate,
        );
        if (response.ok) {
          message.success("Grade added successfully");
          return response.data;
        } else {
          message.error(
            response.msg || response.data?.error || "Failed to add grade",
          );
          return null;
        }
      } catch (error) {
        console.error("Error adding grade:", error);
        message.error("An error occurred while adding grade");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const updateGrade = useCallback(
    async (gradeId, data) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.academics.grade_detail.replace(
          ":id",
          gradeId,
        );
        const response = await Utilities.fetchWithAuth(
          "PUT",
          url,
          data,
          navigate,
        );
        if (response.ok) {
          message.success("Grade updated successfully");
          return response.data;
        } else {
          message.error(response.msg || "Failed to update grade");
          return null;
        }
      } catch (error) {
        console.error("Error updating grade:", error);
        message.error("An error occurred while updating grade");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const deleteGrade = useCallback(
    async (gradeId) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.academics.grade_detail.replace(
          ":id",
          gradeId,
        );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          message.success("Grade deleted successfully");
          return true;
        } else {
          message.error(response.msg || "Failed to delete grade");
          return false;
        }
      } catch (error) {
        console.error("Error deleting grade:", error);
        message.error("An error occurred while deleting grade");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const calculateGrade = useCallback(
    async (gradingSystemId, score) => {
      setLoading(true);
      try {
        const url = `${domainSettings.urls.academics.grading_system_calculate.replace(":id", gradingSystemId)}?score=${score}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          setCalculationResult(response.data);
          return response.data;
        } else {
          message.error(
            response.msg ||
              response.data?.error ||
              "No grade found for this score",
          );
          setCalculationResult(null);
          return null;
        }
      } catch (error) {
        console.error("Error calculating grade:", error);
        message.error("An error occurred while calculating grade");
        setCalculationResult(null);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  return {
    loading,
    gradingSystems,
    grades,
    calculationResult,
    fetchGradingSystems,
    fetchGrades,
    addGradeToSystem,
    updateGrade,
    deleteGrade,
    calculateGrade,
    setCalculationResult,
  };
};

export default useGradingSystems;
